import { forwardRef, useState, type ReactNode } from "react";

import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { Mousewheel, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";

export interface SliderProps {
  /** Each entry is rendered as one slide. The component wraps it in a slide internally. */
  slides: ReactNode[];
  /** Number of slides visible at once. Defaults to 3. */
  slidesPerView?: number;
  /** Gap between slides in px. Defaults to 8. */
  spaceBetween?: number;
  /** Show the ‹ › arrows. They are hidden anyway when every slide already fits. */
  navigation?: boolean;
  /** Loop back to the start after the last slide. Defaults to false. */
  loop?: boolean;
  /** Let the mouse wheel scroll the track (horizontal only). Defaults to false. */
  mousewheel?: boolean;
  /** Fired with the real slide index when a slide is clicked. */
  onSlideClick?: (index: number) => void;
  /** Fired with the active (real) slide index whenever navigation changes it. */
  onActiveIndexChange?: (index: number) => void;
  /** Move the clicked slide into the active (leading) position. Defaults to false. */
  slideToClickedSlide?: boolean;
  className?: string;
}

// Press feedback (the MUI ripple is already on by default) plus a quick scale.
const arrowSx = {
  flexShrink: 0,
  color: "text.secondary",
  transition: "transform .1s ease",
  "&:active": { transform: "scale(0.85)" },
};

/**
 * Presentational carousel built on Swiper. It owns no business logic: callers
 * pass already-rendered slide nodes and decide what a click means.
 *
 * Navigation uses custom MUI buttons wired into Swiper via prevEl/nextEl, so the
 * arrows sit beside the track (not floating over the slides) and get a ripple.
 * Arrows (and loop) only appear when there are more slides than fit on screen.
 */
export const Slider = forwardRef<HTMLDivElement, SliderProps>(function Slider(
  {
    slides,
    slidesPerView = 3,
    spaceBetween = 8,
    navigation = true,
    loop = false,
    mousewheel = false,
    onSlideClick,
    onActiveIndexChange,
    slideToClickedSlide = false,
    className,
  },
  ref,
) {
  // Callback refs via state so Swiper re-wires navigation once the buttons mount.
  const [prevEl, setPrevEl] = useState<HTMLButtonElement | null>(null);
  const [nextEl, setNextEl] = useState<HTMLButtonElement | null>(null);

  const hasOverflow = slides.length > slidesPerView;
  const showNavigation = navigation && hasOverflow;
  const loopEnabled = loop && hasOverflow;

  return (
    <div
      ref={ref}
      className={className}
      style={{ display: "flex", alignItems: "center", gap: 4 }}
    >
      {showNavigation && (
        <IconButton
          ref={setPrevEl}
          size="small"
          aria-label="Previous"
          sx={arrowSx}
        >
          <ChevronLeft fontSize="small" />
        </IconButton>
      )}
      <Swiper
        modules={[Navigation, Mousewheel]}
        navigation={showNavigation ? { prevEl, nextEl } : false}
        loop={loopEnabled}
        // releaseOnEdges hands scrolling back to the page at the first/last
        // slide so the wheel switches slides without trapping page scroll.
        mousewheel={mousewheel ? { releaseOnEdges: true } : false}
        slidesPerView={slidesPerView}
        spaceBetween={spaceBetween}
        onSlideChange={(swiper) => onActiveIndexChange?.(swiper.realIndex)}
        onClick={(swiper) => {
          // Resolve the real index even for loop clones (which carry the attr).
          const attr = swiper.clickedSlide?.getAttribute(
            "data-swiper-slide-index",
          );
          const clicked = attr != null ? Number(attr) : swiper.clickedIndex;
          if (clicked == null || Number.isNaN(clicked)) {
            return;
          }
          // Bring the clicked slide to the leading position so realIndex matches
          // what was clicked (Swiper's built-in slideToClickedSlide only nudges
          // by one when slidesPerView > 1).
          if (slideToClickedSlide) {
            if (loopEnabled) {
              swiper.slideToLoop(clicked);
            } else {
              swiper.slideTo(clicked);
            }
          }
          onSlideClick?.(clicked);
        }}
        style={{ flex: 1, minWidth: 0 }}
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={index}>{slide}</SwiperSlide>
        ))}
      </Swiper>
      {showNavigation && (
        <IconButton ref={setNextEl} size="small" aria-label="Next" sx={arrowSx}>
          <ChevronRight fontSize="small" />
        </IconButton>
      )}
    </div>
  );
});
